import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, PencilRuler } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";

export function ManualEntryDialog({ companyId, years, defaultYear, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [schema, setSchema] = useState(null);
  const [year, setYear] = useState(defaultYear || new Date().getFullYear());
  const [values, setValues] = useState({ sp: {}, ce: {} });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && !schema) api.get("/schema").then((r) => setSchema(r.data));
  }, [open, schema]);

  const loadYear = (y) => {
    setLoading(true);
    api.get(`/companies/${companyId}/statement`, { params: { year: y } })
      .then((r) => setValues({ sp: r.data.sp || {}, ce: r.data.ce || {} }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) loadYear(year);
    // eslint-disable-next-line
  }, [open]);

  const setVal = (group, key, v) => setValues((prev) => ({ ...prev, [group]: { ...prev[group], [key]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/statement`, { fiscal_year: Number(year), sp: values.sp, ce: values.ce });
      toast.success(`Bilancio ${year} salvato`);
      setOpen(false);
      onSaved?.(Number(year));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const renderFields = (list, group) => {
    const groups = {};
    list.forEach((f) => { groups[f.group] = groups[f.group] || []; groups[f.group].push(f); });
    return Object.entries(groups).map(([g, fields]) => (
      <div key={g} className="mb-5">
        <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">{g}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs text-muted-foreground">{f.label}</Label>
              <Input
                type="number" className="font-mono h-9"
                value={values[group][f.key] ?? ""}
                onChange={(e) => setVal(group, f.key, e.target.value)}
                data-testid={`field-${group}-${f.key}`}
                placeholder="0"
              />
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PencilRuler className="h-5 w-5 text-primary" /> Voci Manuali di Bilancio</DialogTitle>
          <DialogDescription>Correggi o inserisci a mano le voci quando l'XBRL è incompleto. I valori inseriti sovrascrivono l'esercizio selezionato.</DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Esercizio</Label>
            <Input type="number" className="w-28 font-mono h-9" value={year}
              onChange={(e) => setYear(e.target.value)} onBlur={() => loadYear(year)}
              data-testid="manual-year-input" />
          </div>
          <div className="flex gap-1 pb-1">
            {(years || []).map((y) => (
              <button key={y} onClick={() => { setYear(y); loadYear(y); }}
                className={`text-xs font-mono px-2 py-1 rounded ${Number(year) === y ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
                data-testid={`manual-year-chip-${y}`}>{y}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento...</div>
        ) : (
          <Tabs defaultValue="sp" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sp" data-testid="manual-tab-sp">Stato Patrimoniale</TabsTrigger>
              <TabsTrigger value="ce" data-testid="manual-tab-ce">Conto Economico</TabsTrigger>
            </TabsList>
            <ScrollArea className="h-[45vh] pr-4 mt-4">
              <TabsContent value="sp">{schema && renderFields(schema.sp, "sp")}</TabsContent>
              <TabsContent value="ce">{schema && renderFields(schema.ce, "ce")}</TabsContent>
            </ScrollArea>
          </Tabs>
        )}

        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="manual-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva Bilancio {year}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
